import React, { memo, useEffect, useMemo } from 'react';
import { ColorNames, SSO_ROOT } from '@/utils/constants';
import { usePortalBoxHandler } from '@/features/shared/HOCs';
import {
  PORTAL_NAVIGATION_BOX_PORTAL_EVENT,
  PORTAL_NAVIGATION_BOX_PORTAL_ID,
  PORTAL_NAVIGATION_KEY,
  PORTAL_NAX_LOADED_EVENT,
  PortalNavigationVariants,
} from '@/features/PortalNavigation/PortalNavigation.constants';
import { usePortalNavigationAvailability } from '@/features/PortalNavigation/hooks/usePortalNavigationAvailability';
import useIsMobile from '@/features/shared/Hooks/useIsMobile';
import { resolveTheme } from '@/utils/resolvers';
import { equals, isEmpty, useWith as fpUserWith } from 'lodash/fp';
import { createPortal } from 'react-dom';
import { ConditionalContentContainer } from '@/features/PortalNavigation/components/ConditionalContentContainer/ConditionalContentContainer';
import BackButtonNavigation from '@/features/PortalNavigation/components/BackButtonNavigation/BackButtonNavigation';
import { BreadcrumbBox } from '@/features';
import PortalNavigation from './PortalNavigation';
import { makeStyles } from '@material-ui/core/styles';
import { withStickyWrapper } from '@/features/PortalNavigation/hoc/withStickyWrapper';
import { triggerEvent } from '@/utils/helpers';
import { withPortalAvailability } from '@/features/PortalNavigation/hoc/withPortalAvailability';

const usePortalNavigationMapperStyles = makeStyles(
  ({ spacing, breakpoints }) => ({
    breadcrumb: {
      '& a': {
        '& p': {
          lineHeight: '1em',
        },
      },
    },
    spacer: {
      flexGrow: 1,
    },
    portalNavigationContainer: {
      display: 'flex',
      flexDirection: 'column',
      paddingTop: spacing(2),

      [breakpoints.up('md')]: {
        gap: spacing(1),
        paddingTop: 0,
        flexDirection: 'row',
        justifyContent: 'flex-start',
      },
    },
    breadcrumbContainer: {
      display: 'flex',
      gap: spacing(1),
      alignItems: 'center',
    },
    backButton: {
      margin: spacing(1, 0),
      // [breakpoints.up('md')]: {
      // },
    },
  }),
  { name: 'PortalNavigationCMSWrapper' },
);

const clearPath = (pathString: string) => pathString.trim().replaceAll('/', '');
const equalPaths = fpUserWith(equals, [clearPath, clearPath]);
const StickyPortalNavigation = withPortalAvailability(withStickyWrapper(PortalNavigation));

const RenderCreatePortal = withPortalAvailability(
  ({ portalNavBox, children }: React.PropsWithChildren<any>) => {
    return portalNavBox ? (
      <>{createPortal(<div>{children}</div>, portalNavBox, PORTAL_NAVIGATION_KEY)}</>
    ) : (
      <>{children}</>
    );
  },
);

export const PortalNavigatorMapper = memo(
  ({
    content = {},
    isPreview,
    contentButtons,
    includeBackButton,
    labels = {},
    presentation = {},
  }: any) => {
    const classes = usePortalNavigationMapperStyles();
    const { navigationItems } = content;
    const { backButtonLabel, portalMenuLabel } = labels;
    const { containerBackgroundColor = ColorNames.charcoal } = presentation;
    const { elementPortalBox: portalNavBox } = usePortalBoxHandler({
      boxId: PORTAL_NAVIGATION_BOX_PORTAL_ID,
      readyEventName: PORTAL_NAVIGATION_BOX_PORTAL_EVENT,
    });
    const portalNavigationAvailability = usePortalNavigationAvailability();
    const isMobile = useIsMobile();

    const isRootPath = equalPaths(window.location.pathname, SSO_ROOT);
    const portalNavigationProps = useMemo(
      () => ({
        label: portalMenuLabel || 'Take me to',
        navigationItems,
        contentButtons,
        'data-datalayer-position': 'portal_navigation',
        'data-datalayer-variant': presentation?.datalayerVariant,
        variant: PortalNavigationVariants.INLINE,
      }),
      [portalMenuLabel, navigationItems, contentButtons, presentation?.datalayerVariant],
    );

    const theme = useMemo(
      () => resolveTheme('bg-color')({ rawVal: containerBackgroundColor }),
      [containerBackgroundColor],
    );

    const isSticky = useMemo(
      () =>
        portalNavigationProps.variant === PortalNavigationVariants.INLINE &&
        !isMobile &&
        !isPreview,
      [portalNavigationProps.variant, isMobile, isPreview],
    );

    const hasPortalContainer = useMemo(
      () => (!isRootPath || !portalNavBox) && !isSticky && !isMobile,
      [isRootPath, portalNavBox, isSticky, isMobile],
    );

    useEffect(() => {
      if (isSticky) {
        triggerEvent({ target: document, eventName: PORTAL_NAX_LOADED_EVENT });
      }
    }, [isSticky]);

    if (isEmpty(labels)) {
      console.warn('"portalNavigation.labels" is empty');
    }

    return (
      <>
        <ConditionalContentContainer
          datalayerVariant={presentation?.datalayerVariant}
          hasPortalContainer={hasPortalContainer}
          containerBackgroundColor={containerBackgroundColor}
        >
          <div data-datalayer-position="breadcrumb" className={classes.portalNavigationContainer}>
            {!isRootPath && (
              <div className={classes.breadcrumbContainer}>
                {includeBackButton && (
                  <BackButtonNavigation
                    className={classes.backButton}
                    theme={theme}
                    buttonLabel={backButtonLabel}
                  />
                )}
                {!isSticky && <BreadcrumbBox theme={theme} className={classes.breadcrumb} />}
              </div>
            )}
            <div className={classes.spacer} />
            {!isSticky ? (
              <RenderCreatePortal
                isPreview={isPreview}
                portalNavBox={portalNavBox}
                enabled={portalNavigationAvailability.enabled || isPreview}
              >
                <PortalNavigation {...portalNavigationProps} horizontalAlignment="right" />
              </RenderCreatePortal>
            ) : (
              <StickyPortalNavigation
                isPreview={isPreview}
                enabled={portalNavigationAvailability.enabled || isPreview}
                {...portalNavigationProps}
                horizontalAlignment="right"
              />
            )}
          </div>
        </ConditionalContentContainer>
      </>
    );
  },
);
